import { BehaviorSubject } from 'rxjs';

class RateLimiter {
  private static instance: RateLimiter;
  private callCount: number = 0;
  private totalApiCalls: number = 0;
  private resetTimer: NodeJS.Timeout | null = null;
  private lastReset: number = Date.now();
  private readonly RATE_LIMIT = 40;
  private readonly RATE_WINDOW = 60 * 1000;
  private readonly QUEUE_DELAY = 1000; // Reduced from 2000ms to 1000ms
  
  private queueSubject: BehaviorSubject<number>;
  private requestQueue: (() => Promise<void>)[] = [];
  private isProcessingQueue = false;
  private lastRequestTime: number = 0;
  private networkErrorCount: number = 0;
  private readonly MAX_NETWORK_ERRORS = 5;
  private isOfflineMode: boolean = false;
  private isSSR: boolean = false;

  private constructor() {
    this.queueSubject = new BehaviorSubject<number>(0);
    this.resetCallCount();
    
    // Check if we're in a server-side rendering environment
    this.isSSR = typeof window === 'undefined';
    
    if (this.isSSR) {
      this.isOfflineMode = true; // Force offline mode in SSR
    } else {
      // Add network status listeners if in browser environment
      window.addEventListener('online', this.handleOnline.bind(this));
      window.addEventListener('offline', this.handleOffline.bind(this));
      
      // Check initial network status
      this.isOfflineMode = !navigator.onLine;
      if (this.isOfflineMode) {
        console.warn('Network appears to be offline. Operating in offline mode.');
      }
    }
  }

  public static getInstance(): RateLimiter {
    if (!RateLimiter.instance) {
      RateLimiter.instance = new RateLimiter();
    }
    return RateLimiter.instance;
  }

  private resetCallCount() {
    if (this.resetTimer) {
      clearInterval(this.resetTimer);
    }

    this.resetTimer = setInterval(() => {
      this.callCount = 0;
      this.lastReset = Date.now();
      this.queueSubject.next(this.callCount);
      
      // Reset network error count periodically
      this.networkErrorCount = 0;
    }, this.RATE_WINDOW);
  }

  private async enforceMinimumDelay(): Promise<void> {
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequestTime;
    
    if (timeSinceLastRequest < this.QUEUE_DELAY) {
      await new Promise(resolve => 
        setTimeout(resolve, this.QUEUE_DELAY - timeSinceLastRequest)
      );
    }
    this.lastRequestTime = Date.now();
  }

  private async processQueue() {
    if (this.isProcessingQueue || this.requestQueue.length === 0) return;
    
    this.isProcessingQueue = true;
    
    while (this.requestQueue.length > 0) {
      const request = this.requestQueue.shift();
      if (request) {
        try {
          await this.enforceMinimumDelay();
          await request();
        } catch (error) {
          // Track network errors
          if (this.isNetworkError(error)) {
            this.networkErrorCount++;
            if (this.networkErrorCount >= this.MAX_NETWORK_ERRORS) {
              this.isOfflineMode = true;
            }
          }
        }
      }
    }
    
    this.isProcessingQueue = false;
  }

  private isNetworkError(error: any): boolean {
    return (
      error.message === 'Failed to fetch' ||
      error.message === 'Network request failed' ||
      error.message?.includes('network') ||
      error.name === 'TypeError' && error.message?.includes('fetch')
    );
  }

  private handleOnline() {
    console.log('Network connection restored. Resuming normal operation.');
    this.isOfflineMode = false;
    this.networkErrorCount = 0;
  }

  private handleOffline() {
    console.warn('Network connection lost. Switching to offline mode.');
    this.isOfflineMode = true;
  }

  public async checkRateLimit(): Promise<boolean> {
    // If in SSR mode or offline mode, immediately resolve with a warning
    if (this.isSSR || this.isOfflineMode) {
      if (this.isSSR) {
        console.warn('Operating in SSR mode. Using cached data if available.');
      } else {
        console.warn('Operating in offline mode. Using cached data if available.');
      }
      return true;
    }
    
    return new Promise((resolve, reject) => {
      const makeRequest = async () => {
        try {
          const now = Date.now();
          const timeSinceReset = now - this.lastReset;
          
          if (timeSinceReset >= this.RATE_WINDOW) {
            this.callCount = 0;
            this.lastReset = now;
          }

          if (this.callCount >= this.RATE_LIMIT) {
            reject(new Error('Rate limit exceeded'));
            return;
          }

          await this.enforceMinimumDelay();
          this.callCount++;
          this.totalApiCalls++;
          this.queueSubject.next(this.callCount);
          resolve(true);
        } catch (error) {
          reject(error);
        }
      };

      this.requestQueue.push(makeRequest);
      this.processQueue();
    });
  }

  public getCallCount(): number {
    return this.callCount;
  }

  public getTotalApiCalls(): number {
    return this.totalApiCalls;
  }

  public getRemainingCalls(): number {
    return Math.max(0, this.RATE_LIMIT - this.callCount);
  }

  public isOffline(): boolean {
    return this.isOfflineMode || this.isSSR;
  }

  public setOfflineMode(isOffline: boolean): void {
    this.isOfflineMode = isOffline;
    if (!isOffline) {
      this.networkErrorCount = 0;
    }
    console.log(`Manually ${isOffline ? 'enabled' : 'disabled'} offline mode`);
  }

  public destroy() {
    if (this.resetTimer) {
      clearInterval(this.resetTimer);
      this.resetTimer = null;
    }
    
    if (!this.isSSR) {
      window.removeEventListener('online', this.handleOnline.bind(this));
      window.removeEventListener('offline', this.handleOffline.bind(this));
    }
    
    this.queueSubject.complete();
    this.requestQueue = [];
    this.isProcessingQueue = false;
    this.callCount = 0;
    this.totalApiCalls = 0;
    this.lastRequestTime = 0;
    this.networkErrorCount = 0;
    this.isOfflineMode = false;
  }

  public get queue$() {
    return this.queueSubject.asObservable();
  }
}

export const rateLimiter = RateLimiter.getInstance();